'use client';

import { extractTextFromExcel } from '@ctrl-enter/tools/utils/document-extraction';
import { createLogger } from '@ctrl-enter/tools/utils';
import { OrgPersonalizationSettings, PersonalizationMode } from '@ctrl-enter/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Brain,
  Briefcase,
  Building2,
  Check,
  Code,
  Edit3,
  FileInput,
  FileText,
  GitMerge,
  MessageSquare,
  RotateCcw,
  Save,
  Settings,
  Sparkles,
  Stethoscope,
  Target,
  Upload,
  User,
  UserCircle2,
  X,
} from 'lucide-react';
import { PDFParse } from 'pdf-parse';
import * as React from 'react';
import { useAuth } from '../auth';
import taxonomyCodesData from '../data/taxonomy-codes.json';
import {
  gatewayQueryKeys,
  useOrgPersonalizationQuery,
  useUpdateOrgPersonalizationMutation,
} from '../hooks/use-gateway-queries';
import {
  useActiveMode,
  useOrgSettings,
  useRestartOnboarding,
  useSetActiveMode,
  useSetOrgSettings,
  useSetUserSettings,
  useUpdateUserSettings,
  useUserSettings,
} from '../stores/personalization-store';
import { extractCptCodesFromText } from '../utils/cpt-extraction';
import { computeEffectivePersonalization } from '../utils/personalization';
import { toast } from '../utils/toast';
import { Button } from './button';
import { ChipInput, type ChipInputOption } from './chip-input';
import { InfoRow, InfoSection, InfoTextBlock } from './info-section';
import { LoginRegisterPanel } from './login-register-panel';

const logger = createLogger('client:component:personalization-panel');

export type PersonalizationPanelProps = {
  className?: string;
};

/**
 * PersonalizationPanel component
 *
 * Implements hybrid preset system:
 * - Mode 'org': Use organization defaults (read-only)
 * - Mode 'custom': Use user's custom settings (editable with org fallback)
 *
 * Admin users can edit organization defaults in a separate section
 */
export const PersonalizationPanel = React.forwardRef<HTMLDivElement, PersonalizationPanelProps>(
  ({ className }, ref) => {
    const { isAuthenticated, user } = useAuth();
    const queryClient = useQueryClient();

    // Store state
    const activeMode = useActiveMode();
    const userSettings = useUserSettings();
    const orgSettings = useOrgSettings();
    const setActiveMode = useSetActiveMode();
    const setUserSettings = useSetUserSettings();
    const updateUserSettings = useUpdateUserSettings();
    const setOrgSettings = useSetOrgSettings();

    // Admin check
    const isAdmin = user?.role === 'admin';

    // Organization check - does user belong to an organization?
    const hasOrganization = !!(user?.organizationId && user.organizationId.trim());

    // Safety check: Force custom mode if user doesn't have an organization
    React.useEffect(() => {
      if (isAuthenticated && !hasOrganization && activeMode === 'org') {
        logger.info(
          '⚠️ [PERSONALIZATION-PANEL] User has no organization but mode is "org", forcing to "custom"'
        );
        setActiveMode('custom');
      }
    }, [isAuthenticated, hasOrganization, activeMode, setActiveMode]);

    // Taxonomy options from static data
    const taxonomyOptions: ChipInputOption[] = React.useMemo(
      () => taxonomyCodesData as ChipInputOption[],
      []
    );

    // Local form state for user settings
    const [localDisplayName, setLocalDisplayName] = React.useState(userSettings.displayName || '');
    const [localJobTitle, setLocalJobTitle] = React.useState(userSettings.jobTitle || '');
    const [localSpecialty, setLocalSpecialty] = React.useState(
      userSettings.specialtyOverride || ''
    );
    const [localAdditionalCpts, setLocalAdditionalCpts] = React.useState<string[]>(
      userSettings.additionalCptCodes || []
    );
    const [localAdditionalTaxonomies, setLocalAdditionalTaxonomies] = React.useState<string[]>(
      userSettings.additionalTaxonomies || []
    );
    const [localCustomInstructions, setLocalCustomInstructions] = React.useState(
      userSettings.customInstructions || ''
    );
    const [localOtherDetails, setLocalOtherDetails] = React.useState(
      userSettings.otherDetails || ''
    );

    // Local form state for org settings (admin only)
    const [isEditingOrg, setIsEditingOrg] = React.useState(false);
    const [localOrgName, setLocalOrgName] = React.useState('');
    const [localOrgSpecialty, setLocalOrgSpecialty] = React.useState('');
    const [localOrgCpts, setLocalOrgCpts] = React.useState<string[]>([]);
    const [localOrgTaxonomies, setLocalOrgTaxonomies] = React.useState<string[]>([]);
    const [localOrgOtherDetails, setLocalOrgOtherDetails] = React.useState('');
    const [isUploadingCsv, setIsUploadingCsv] = React.useState(false);
    const [csvUploadError, setCsvUploadError] = React.useState<string | null>(null);

    // NOTE: User settings are NOT fetched from API - they are stored CLIENT-SIDE ONLY in Zustand
    // API returns empty {} for user settings by design (MVP)
    // See personalization-store.ts for client-side persistence

    // Fetch org settings (only for admins who have an organization)
    const {
      data: orgData,
      isLoading: isLoadingOrg,
      error: orgError,
    } = useOrgPersonalizationQuery(isAdmin && hasOrganization);

    // Update org settings mutation (admin only)
    const updateOrgMutation = useUpdateOrgPersonalizationMutation();

    // NOTE: User settings are stored CLIENT-SIDE ONLY (Zustand persist)
    // Do NOT sync from API as backend always returns empty {}
    // User settings are only updated when explicitly saved via the form

    // Sync local form state when userSettings changes
    React.useEffect(() => {
      logger.info(
        '📝 [PERSONALIZATION-PANEL] Syncing local form state with userSettings:',
        JSON.stringify(userSettings, null, 2)
      );
      setLocalDisplayName(userSettings.displayName || '');
      setLocalJobTitle(userSettings.jobTitle || '');
      setLocalSpecialty(userSettings.specialtyOverride || '');
      setLocalAdditionalCpts(userSettings.additionalCptCodes || []);
      setLocalAdditionalTaxonomies(userSettings.additionalTaxonomies || []);
      setLocalCustomInstructions(userSettings.customInstructions || '');
      setLocalOtherDetails(userSettings.otherDetails || '');
    }, [userSettings]);

    // Sync org settings from API to store
    React.useEffect(() => {
      if (orgData?.body.settings) {
        setOrgSettings(orgData.body.settings);
      } else if (orgData?.body.hasSettings === false) {
        setOrgSettings(null);
      }
    }, [orgData, setOrgSettings]);

    // Force custom mode if user has no organization
    React.useEffect(() => {
      if (!hasOrganization && activeMode === 'org') {
        logger.info('⚠️ [PERSONALIZATION] User has no org, forcing custom mode');
        setActiveMode('custom');
      }
    }, [hasOrganization, activeMode, setActiveMode]);

    // Compute effective settings for preview
    const effectiveSettings = React.useMemo(() => {
      return computeEffectivePersonalization({
        activeMode,
        userSettings,
        orgSettings,
        user,
      });
    }, [activeMode, userSettings, orgSettings, user]);

    // Handle mode switch
    const handleModeChange = (mode: PersonalizationMode) => {
      setActiveMode(mode);
      toast.success(
        'Personalization Mode',
        `Switched to ${mode === 'org' ? 'Organization Defaults' : 'Custom Settings'}`
      );
    };

    // Handle save user settings
    const handleSaveUserSettings = () => {
      const updated = {
        displayName: localDisplayName || undefined,
        jobTitle: localJobTitle || undefined,
        specialtyOverride: localSpecialty || undefined,
        additionalCptCodes: localAdditionalCpts.length > 0 ? localAdditionalCpts : undefined,
        additionalTaxonomies:
          localAdditionalTaxonomies.length > 0 ? localAdditionalTaxonomies : undefined,
        customInstructions: localCustomInstructions || undefined,
        otherDetails: localOtherDetails || undefined,
      };

      updateUserSettings(updated);
      toast.success('Settings Saved', 'Your custom settings have been saved');
    };

    // Handle file upload for CPT codes (supports CSV, Excel, PDF, TXT)
    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsUploadingCsv(true);
      setCsvUploadError(null);

      try {
        const extension = file.name.split('.').pop()?.toLowerCase();
        let text = '';

        logger.info('📄 [FILE-UPLOAD] Processing file:', {
          filename: file.name,
          size: file.size,
          type: file.type,
          extension,
        });

        // Parse file based on type
        switch (extension) {
          case 'csv':
          case 'txt':
            // Plain text files
            text = await file.text();
            logger.info('📝 [FILE-UPLOAD] Parsed as plain text');
            break;

          case 'xlsx':
          case 'xls': {
            const xlsxBuffer = await file.arrayBuffer();
            const excelResult = await extractTextFromExcel(xlsxBuffer, file.name);
            if (!excelResult.success) {
              throw new Error(excelResult.error);
            }
            text = excelResult.text;
            logger.info('📊 [FILE-UPLOAD] Parsed Excel:', {
              sheets: excelResult.metadata?.sheetCount,
              textLength: text.length,
            });
            break;
          }

          case 'pdf': {
            // PDF files - extract text content
            const pdfBuffer = await file.arrayBuffer();
            const pdfParser = new PDFParse({ data: Buffer.from(pdfBuffer) });
            const pdfResult = await pdfParser.getText();
            text = pdfResult.text;
            logger.info('📄 [FILE-UPLOAD] Parsed PDF:', {
              textLength: text.length,
            });
            break;
          }

          default:
            throw new Error(
              `Unsupported file format: .${extension}. Please use CSV, Excel (.xlsx/.xls), PDF, or Plain Text (.txt) files.`
            );
        }

        if (!text || text.trim().length === 0) {
          setCsvUploadError('File appears to be empty or could not be read.');
          setIsUploadingCsv(false);
          return;
        }

        // Smart CPT code extraction with multiple strategies
        const extractedCodes = extractCptCodesFromText(text);

        logger.info('🔢 [FILE-UPLOAD] Extracted codes:', {
          total: extractedCodes.length,
          sample: extractedCodes.slice(0, 10),
          strategies: extractedCodes.length > 0 ? 'Success' : 'No codes found',
        });

        if (extractedCodes.length === 0) {
          setCsvUploadError(
            'No valid CPT codes found. Please ensure your file contains 5-digit medical procedure codes (e.g., 99213, 45378).'
          );
          setIsUploadingCsv(false);
          return;
        }

        // Merge with existing codes (avoid duplicates)
        const existingSet = new Set(localOrgCpts);
        const newCodes = extractedCodes.filter((code: string) => !existingSet.has(code));
        const mergedCodes = [...localOrgCpts, ...newCodes].sort();

        setLocalOrgCpts(mergedCodes);

        toast.success(
          'CPT Codes Uploaded',
          `Added ${newCodes.length} new codes from ${extension?.toUpperCase()} (${extractedCodes.length} total found, ${extractedCodes.length - newCodes.length} duplicates skipped)`
        );

        logger.info('✅ [FILE-UPLOAD] Codes merged:', {
          existing: localOrgCpts.length,
          new: newCodes.length,
          total: mergedCodes.length,
          fileType: extension,
        });
      } catch (error) {
        logger.error('❌ [FILE-UPLOAD] Error:', error);
        setCsvUploadError(error instanceof Error ? error.message : 'Failed to process file');
      } finally {
        setIsUploadingCsv(false);
        // Reset file input
        event.target.value = '';
      }
    };

    // Handle start editing org settings
    const handleStartEditingOrg = () => {
      if (orgSettings) {
        setLocalOrgName(orgSettings.organizationDisplayName);
        setLocalOrgSpecialty(orgSettings.primarySpecialty);
        setLocalOrgCpts(orgSettings.commonCptCodes);
        setLocalOrgTaxonomies(orgSettings.providerTaxonomies);
        setLocalOrgOtherDetails(orgSettings.otherDetails || '');
      } else {
        setLocalOrgName('');
        setLocalOrgSpecialty('');
        setLocalOrgCpts([]);
        setLocalOrgTaxonomies([]);
        setLocalOrgOtherDetails('');
      }
      setIsEditingOrg(true);
      // Clear CSV upload errors when opening form
      setCsvUploadError(null);
    };

    // Handle save org settings
    const handleSaveOrgSettings = async () => {
      const trimmedOtherDetails = localOrgOtherDetails.trim();
      const updated: OrgPersonalizationSettings = {
        organizationDisplayName: localOrgName,
        primarySpecialty: localOrgSpecialty,
        commonCptCodes: localOrgCpts,
        providerTaxonomies: localOrgTaxonomies,
        // Always send otherDetails (empty string if cleared, actual value if present)
        // This ensures the field gets included in JSON and can clear the WorkOS metadata
        otherDetails: trimmedOtherDetails,
        personalizedAt: new Date().toISOString(),
      };

      logger.info('💾 [PERSONALIZATION] Saving org settings:', {
        hasOtherDetails: !!trimmedOtherDetails,
        otherDetailsValue: trimmedOtherDetails,
        otherDetailsLength: trimmedOtherDetails.length,
        settingsKeys: Object.keys(updated),
        settings: updated,
      });

      try {
        await updateOrgMutation.mutateAsync({ body: { settings: updated } });

        // Refresh org settings query
        await queryClient.invalidateQueries({
          queryKey: gatewayQueryKeys.personalization.org,
        });

        setIsEditingOrg(false);
        toast.success('Organization Settings Saved', 'Organization defaults have been updated');
      } catch (error) {
        toast.error('Save Failed', 'Failed to save organization settings');
        logger.error('Failed to save org settings:', error);
      }
    };

    // Show login prompt if not authenticated
    if (!isAuthenticated || !user) {
      return (
        <div ref={ref} className={className}>
          <LoginRegisterPanel />
        </div>
      );
    }

    const restartOnboarding = useRestartOnboarding();

    const handleRunOnboarding = () => {
      restartOnboarding();
      toast.success('Starting onboarding flow...');
    };

    return (
      <div ref={ref} className={`flex flex-col gap-8 p-1 sm:p-6 ${className || ''}`}>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-semibold">Personalization</h2>
            <p className="text-secondary-enhanced">
              Customize how the AI assistant understands your role and organization
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRunOnboarding}
            className="gap-2 w-full sm:w-auto sm:flex-shrink-0"
          >
            <RotateCcw className="w-4 h-4" />
            Run Onboarding
          </Button>
        </div>

        {/* Mode Selection */}
        {hasOrganization ? (
          <InfoSection title="Choose Your Preset" titleIcon={<Target className="w-5 h-5" />}>
            <p className="text-secondary-enhanced -mt-2 mb-2">
              Select how the AI should understand your context
            </p>

            <div className="flex flex-col gap-2.5">
              {/* Organization Defaults Option */}
              <label
                className={`
 relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-300
 ${
   activeMode === 'org'
     ? 'border-2 border-primary glass-surface-active shadow-md'
     : 'glass-surface hover:glass-surface-hover'
 }
 `}
              >
                <input
                  type="radio"
                  name="personalization-mode"
                  checked={activeMode === 'org'}
                  onChange={() => handleModeChange('org')}
                  className="sr-only"
                />
                <div
                  className={`
 flex-shrink-0 w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center transition-all duration-300
 ${activeMode === 'org' ? 'border-primary bg-primary shadow-sm' : 'border-border'}
 `}
                >
                  {activeMode === 'org' && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Building2
                      className={`w-4 h-4 flex-shrink-0 ${activeMode === 'org' ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                    <span className="font-semibold text-sm">Organization Defaults</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
                        activeMode === 'org'
                          ? 'bg-primary/20 text-primary border-primary/40'
                          : 'bg-primary/10 text-primary/70 border-primary/25'
                      }`}
                    >
                      Recommended
                    </span>
                  </div>
                  <p className="text-secondary-enhanced text-xs leading-relaxed">
                    Use organization-wide settings configured by your admin for consistency across
                    your team
                  </p>
                </div>
              </label>

              {/* My Custom Settings Option */}
              <label
                className={`
 relative flex items-start gap-3 p-3.5 rounded-xl cursor-pointer transition-all duration-300
 ${
   activeMode === 'custom'
     ? 'border-2 border-primary glass-surface-active shadow-md'
     : 'glass-surface hover:glass-surface-hover'
 }
 `}
              >
                <input
                  type="radio"
                  name="personalization-mode"
                  checked={activeMode === 'custom'}
                  onChange={() => handleModeChange('custom')}
                  className="sr-only"
                />
                <div
                  className={`
 flex-shrink-0 w-5 h-5 mt-0.5 rounded-full border-2 flex items-center justify-center transition-all duration-300
 ${activeMode === 'custom' ? 'border-primary bg-primary shadow-sm' : 'border-border'}
 `}
                >
                  {activeMode === 'custom' && (
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <UserCircle2
                      className={`w-4 h-4 flex-shrink-0 ${activeMode === 'custom' ? 'text-primary' : 'text-muted-foreground'}`}
                    />
                    <span className="font-semibold text-sm">My Custom Settings</span>
                  </div>
                  <p className="text-secondary-enhanced text-xs leading-relaxed">
                    Override and customize settings to match your specific role, workflow, and
                    preferences
                  </p>
                </div>
              </label>
            </div>
          </InfoSection>
        ) : (
          <InfoSection
            title="Personal Settings Only"
            titleIcon={<AlertCircle className="w-5 h-5" />}
          >
            <p className="text-secondary-enhanced">
              You're not part of an organization. Configure your personal settings below to
              customize how the AI assistant works for you.
            </p>
          </InfoSection>
        )}

        {/* Organization Defaults (Read-Only View) */}
        {orgSettings && (
          <InfoSection
            title="Organization Defaults"
            titleIcon={<Building2 className="w-5 h-5" />}
            badge="(read-only)"
          >
            {/* Core Metadata */}
            <div className="flex flex-col gap-3">
              <InfoRow label="Organization" value={orgSettings.organizationDisplayName} />
              <InfoRow label="Medical Specialty" value={orgSettings.primarySpecialty} />

              {/* CPT Codes as Pills */}
              {orgSettings.commonCptCodes.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground font-medium">Common CPT Codes:</span>
                  <div className="flex flex-wrap gap-2">
                    {orgSettings.commonCptCodes.map(code => (
                      <span key={code} className="glass-pill">
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Taxonomies as Pills */}
              {orgSettings.providerTaxonomies.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-muted-foreground font-medium">Taxonomies:</span>
                  <div className="flex flex-wrap gap-2">
                    {orgSettings.providerTaxonomies.map(tax => (
                      <span key={tax} className="glass-pill">
                        {tax}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* AI Behavior Instructions - Highlighted Section */}
            {orgSettings.otherDetails && (
              <InfoSection
                variant="nested"
                title="AI Behavior & Context"
                titleIcon={<Brain className="w-4 h-4" />}
              >
                <InfoTextBlock label="Other Details">{orgSettings.otherDetails}</InfoTextBlock>
              </InfoSection>
            )}

            {activeMode === 'org' && (
              <div className="text-sm text-primary font-medium mt-2 flex items-center gap-2">
                <Check className="w-4 h-4" />
                These settings are currently active
              </div>
            )}
          </InfoSection>
        )}

        {!orgSettings && !isLoadingOrg && hasOrganization && (
          <InfoSection>
            <p className="text-secondary-enhanced">
              No organization defaults configured yet.
              {isAdmin && ' Use the admin section below to set them up.'}
            </p>
          </InfoSection>
        )}

        {/* Custom Settings (Editable) */}
        {activeMode === 'custom' && (
          <InfoSection title="My Custom Settings" titleIcon={<UserCircle2 className="w-5 h-5" />}>
            {/* Field Behavior Legend */}
            <div className="p-4 glass-surface-bg glass-surface-border rounded-xl border border-dashed border-border/50 -mt-2">
              <div className="text-sm font-semibold text-foreground mb-3">Field Behavior Guide</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* User-Only */}
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2.5 py-1 rounded font-medium text-xs w-[130px] text-center flex items-center justify-center gap-1.5 border border-blue-600/40 dark:border-blue-500/25">
                    <UserCircle2 className="w-3 h-3" />
                    User-Only
                  </span>
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    Your personal field
                  </span>
                </div>

                {/* Combines */}
                <div className="flex items-center gap-2.5">
                  <span className="text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2.5 py-1 rounded font-medium text-xs w-[130px] text-center flex items-center justify-center gap-1.5 border border-blue-600/40 dark:border-blue-500/25">
                    <GitMerge className="w-3 h-3" />
                    Combines
                  </span>
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    Merged with org values
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-8">
              {/* Display Name Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Display Name
                </label>
                <input
                  type="text"
                  value={localDisplayName}
                  onChange={e => setLocalDisplayName(e.target.value)}
                  placeholder={user?.name || 'Your name'}
                  className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                <span className="text-xs text-muted-foreground">
                  Leave blank to use: {user?.name || 'your name'}
                </span>
              </div>

              {/* Job Role Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Your Job Role
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <UserCircle2 className="w-3 h-3" />
                    User-Only
                  </span>
                </label>
                <input
                  type="text"
                  value={localJobTitle}
                  onChange={e => setLocalJobTitle(e.target.value)}
                  placeholder="e.g., RCM Specialist, Billing Manager, Medical Coder"
                  className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                <span className="text-xs text-muted-foreground">
                  Your role within the organization (different from medical specialty)
                </span>
              </div>

              {/* Medical Specialty Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Stethoscope className="w-4 h-4" />
                  Your Medical Specialty (Optional)
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <GitMerge className="w-3 h-3" />
                    Combines
                  </span>
                </label>
                <input
                  type="text"
                  value={localSpecialty}
                  onChange={e => setLocalSpecialty(e.target.value)}
                  placeholder={
                    orgSettings?.primarySpecialty
                      ? `e.g., Pediatric ${orgSettings.primarySpecialty}`
                      : 'e.g., Pediatric Gastroenterology, Interventional Cardiology'
                  }
                  className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                <span className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Adds to the organization's department specialty. Leave
                  blank to use only org default: {orgSettings?.primarySpecialty || 'Not set'}
                </span>
                {orgSettings?.primarySpecialty &&
                  localSpecialty &&
                  localSpecialty !== orgSettings.primarySpecialty && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      Will appear as: {localSpecialty} (Department: {orgSettings.primarySpecialty})
                    </span>
                  )}
              </div>

              {/* CPT Codes Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Additional CPT Codes
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <GitMerge className="w-3 h-3" />
                    Combines
                  </span>
                </label>
                <ChipInput
                  value={localAdditionalCpts}
                  onChange={setLocalAdditionalCpts}
                  freeSolo={true}
                  placeholder="Type CPT code and press Enter (e.g., 99205, 99215)"
                  helperText={
                    orgSettings?.commonCptCodes?.length
                      ? `These will be COMBINED with org codes: ${orgSettings.commonCptCodes.join(', ')}`
                      : 'Type CPT codes and press Enter or comma to add'
                  }
                />
              </div>

              {/* Additional Taxonomies Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Additional Taxonomies
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <GitMerge className="w-3 h-3" />
                    Combines
                  </span>
                </label>
                <ChipInput
                  value={localAdditionalTaxonomies}
                  onChange={setLocalAdditionalTaxonomies}
                  freeSolo={true}
                  placeholder="Type taxonomy code and press Enter (e.g., 193400000X)"
                  helperText={
                    orgSettings?.providerTaxonomies?.length
                      ? `These will be COMBINED with org taxonomies: ${orgSettings.providerTaxonomies.join(', ')}`
                      : 'Type NUCC Healthcare Provider Taxonomy codes and press Enter or comma to add'
                  }
                />
              </div>

              {/* Custom Instructions Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Custom Instructions
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <UserCircle2 className="w-3 h-3" />
                    User-Only
                  </span>
                </label>
                <textarea
                  value={localCustomInstructions}
                  onChange={e => setLocalCustomInstructions(e.target.value)}
                  placeholder="e.g., Always be concise, Focus on billing accuracy, Use medical terminology"
                  rows={3}
                  className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm resize-vertical focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                <span className="text-xs text-muted-foreground">
                  Your personal AI behavior, style, and tone preferences
                </span>
              </div>

              {/* Other Details Section */}
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <FileInput className="w-4 h-4" />
                  Other Details
                  <span className="text-xs font-normal text-blue-800 dark:text-blue-300 bg-blue-600/15 dark:bg-blue-500/15 px-2 py-0.5 rounded flex items-center gap-1 border border-blue-600/40 dark:border-blue-500/25">
                    <GitMerge className="w-3 h-3" />
                    Combines
                  </span>
                </label>
                <textarea
                  value={localOtherDetails}
                  onChange={e => setLocalOtherDetails(e.target.value)}
                  placeholder="Any additional information about yourself, your workflow, or preferences..."
                  rows={4}
                  className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm resize-vertical focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                />
                <span className="text-xs text-muted-foreground">
                  Your details will be <strong>combined</strong> with organization details and
                  provided to the AI together
                </span>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSaveUserSettings}
                variant="primary"
                className="w-full sm:w-auto sm:self-start"
              >
                <Save className="w-4 h-4" />
                Save My Settings
              </Button>
            </div>
          </InfoSection>
        )}

        {/* Effective Settings Preview */}
        <InfoSection
          variant="primary"
          title="Active Settings Preview"
          titleIcon={<Sparkles className="w-5 h-5" />}
        >
          {/* Core Metadata */}
          <div className="flex flex-col gap-3">
            <InfoRow label="AI will address you as" value={effectiveSettings.name} />
            {effectiveSettings.jobTitle && (
              <InfoRow label="Your Job Role" value={effectiveSettings.jobTitle} />
            )}
            <InfoRow label="Organization" value={effectiveSettings.organizationName} />
            <InfoRow label="Medical Specialty" value={effectiveSettings.specialty} />

            {/* CPT Codes as Pills */}
            {effectiveSettings.cptCodes.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-muted-foreground font-medium">Common CPT Codes:</span>
                <div className="flex flex-wrap gap-2">
                  {effectiveSettings.cptCodes.map(code => (
                    <span key={code} className="glass-pill">
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Taxonomies as Pills */}
            {effectiveSettings.taxonomies.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-muted-foreground font-medium">Taxonomies:</span>
                <div className="flex flex-wrap gap-2">
                  {effectiveSettings.taxonomies.map(tax => (
                    <span key={tax} className="glass-pill">
                      {tax}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* AI Behavior Instructions - Highlighted Section */}
          {(effectiveSettings.customInstructions || effectiveSettings.otherDetails) && (
            <InfoSection
              variant="nested"
              title="AI Behavior & Context"
              titleIcon={<Brain className="w-4 h-4" />}
            >
              {effectiveSettings.customInstructions && (
                <InfoTextBlock label="Custom Instructions" className="mb-3">
                  {effectiveSettings.customInstructions}
                </InfoTextBlock>
              )}

              {effectiveSettings.otherDetails && (
                <InfoTextBlock label="Other Details">
                  {effectiveSettings.otherDetails}
                </InfoTextBlock>
              )}
            </InfoSection>
          )}
        </InfoSection>

        {/* Admin Section */}
        {isAdmin && hasOrganization && (
          <InfoSection
            title="Manage Organization Defaults"
            titleIcon={<Settings className="w-5 h-5" />}
            badge="Admin Only"
          >
            {!isEditingOrg ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-muted-foreground">
                  Configure default settings for all users in your organization.
                </p>
                <Button
                  onClick={handleStartEditingOrg}
                  variant="outline"
                  className="w-full sm:w-auto sm:self-start"
                >
                  <Edit3 className="w-4 h-4" />
                  {orgSettings ? 'Edit Organization Settings' : 'Set Up Organization Settings'}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {/* Organization Name Section */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Organization Name*
                  </label>
                  <input
                    type="text"
                    value={localOrgName}
                    onChange={e => setLocalOrgName(e.target.value)}
                    placeholder="e.g., ABC Medical Group"
                    className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  />
                </div>

                {/* Primary Specialty Section */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Stethoscope className="w-4 h-4" />
                    Primary Medical Specialty*
                  </label>
                  <input
                    type="text"
                    value={localOrgSpecialty}
                    onChange={e => setLocalOrgSpecialty(e.target.value)}
                    placeholder="e.g., Gastroenterology, Family Medicine, Cardiology"
                    className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  />
                  <span className="text-xs text-muted-foreground">
                    The medical practice area/specialty of your organization
                  </span>
                </div>

                {/* CPT Codes Section */}
                <div className="flex flex-col gap-3">
                  <ChipInput
                    label={
                      <span className="flex items-center gap-2">
                        <Code className="w-4 h-4" />
                        Common CPT Codes*
                      </span>
                    }
                    value={localOrgCpts}
                    onChange={setLocalOrgCpts}
                    freeSolo={true}
                    placeholder="Type CPT code and press Enter (e.g., 99213, 99214)"
                    helperText="Type CPT codes and press Enter or comma to add. Or upload CSV below."
                  />

                  {/* CSV Upload */}
                  <div className="flex flex-col gap-2 p-3 border border-dashed border-border rounded-lg bg-muted/20">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Upload CPT Codes from document
                      </label>
                      {localOrgCpts.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          {localOrgCpts.length} codes loaded
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls,.pdf,.txt"
                        onChange={handleFileUpload}
                        disabled={isUploadingCsv}
                        className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer disabled:opacity-50"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground">
                      Upload billing documents with CPT codes (CSV, Excel, PDF, or TXT). Files
                      should contain 5-digit codes (e.g., 99213, 45378). Codes can be in any format,
                      with or without headers.
                    </span>
                    {csvUploadError && (
                      <span className="text-xs text-red-600 dark:text-red-400">
                        Error: {csvUploadError}
                      </span>
                    )}
                    {isUploadingCsv && (
                      <span className="text-xs text-primary">Processing file...</span>
                    )}
                  </div>
                </div>

                {/* Taxonomies Section */}
                <ChipInput
                  label={
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Provider Taxonomies (Optional)
                    </span>
                  }
                  value={localOrgTaxonomies}
                  onChange={setLocalOrgTaxonomies}
                  options={taxonomyOptions}
                  placeholder="Search taxonomy codes (e.g., Gastroenterology, 207RG0100X)"
                  helperText="Search and select from standard NUCC taxonomy codes"
                />

                {/* Other Details Section */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileInput className="w-4 h-4" />
                    Other Details (Optional)
                  </label>
                  <textarea
                    value={localOrgOtherDetails}
                    onChange={e => setLocalOrgOtherDetails(e.target.value)}
                    placeholder="Additional information about your organization, workflow, policies, or any context relevant for all users..."
                    rows={4}
                    className="px-3 py-2.5 border border-border/20 dark:border-border rounded-xl bg-muted/30 text-sm resize-vertical focus:bg-background focus:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  />
                  <span className="text-xs text-muted-foreground">
                    Unstructured paragraph for organization-wide context
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleSaveOrgSettings}
                    variant="primary"
                    disabled={
                      !localOrgName ||
                      !localOrgSpecialty ||
                      localOrgCpts.length === 0 ||
                      updateOrgMutation.isPending
                    }
                    className="w-full sm:w-auto"
                  >
                    <Save className="w-4 h-4" />
                    {updateOrgMutation.isPending ? 'Saving...' : 'Save Organization Settings'}
                  </Button>
                  <Button
                    onClick={() => setIsEditingOrg(false)}
                    variant="outline"
                    disabled={updateOrgMutation.isPending}
                    className="w-full sm:w-auto"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </InfoSection>
        )}
      </div>
    );
  }
);

PersonalizationPanel.displayName = 'PersonalizationPanel';
